import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Alert,
  Snackbar,
} from '@mui/material';
import Header from '../components/Header';
import ShiftTable from '../components/ShiftTable';
import ReportPanel from '../components/ReportPanel';
import { shiftsApi, featureFlagsApi } from '../services/api';
import { useTranslation } from 'react-i18next';

const REFRESH_INTERVAL = 60000; // 1 minute in milliseconds

const Dashboard = () => {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [filters, setFilters] = useState({
    startDate: new Date().toISOString(),  // Initialize with today's date
    endDate: new Date().toISOString(),    // Initialize with today's date
    search: '',  // Keep empty search for API compatibility
    personFilter: '',  // Initialize empty person filter
    companyFilter: '', // Initialize empty company filter
    checkInLocationFilter: '', // Initialize empty check-in location filter
    checkOutLocationFilter: '', // Initialize empty check-out location filter
    sortBy: '',  // Initialize with no sorting
    sortDirection: 'asc',  // Default sort direction
    activeShifts: false  // Initialize active shifts filter as false
  });
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [showTruckEnter, setShowTruckEnter] = useState(true);
  const [showVanEnter, setShowVanEnter] = useState(true);
  const { t } = useTranslation();

  const loadFeatureFlags = async () => {
    try {
      const response = await featureFlagsApi.getFeatureFlags();
      const flags = response.data;
      setShowTruckEnter(flags.find(f => f.featureName === 'SHOW_TRUCK_ENTER_ENTRIES')?.featureEnabled ?? true);
      setShowVanEnter(flags.find(f => f.featureName === 'SHOW_VAN_ENTER_ENTRIES')?.featureEnabled ?? true);
    } catch (error) {
      console.error('Failed to load feature flags:', error);
      setError('Failed to load feature flags');
    }
  };

  useEffect(() => {
    loadFeatureFlags();
  }, []);

  const handleFilterChange = (newFilters) => {
    setPage(0); // Reset to first page
    setFilters(newFilters);
  };

  const fetchShifts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await shiftsApi.getShifts(
        page,
        filters.search,
        filters.startDate,
        filters.endDate,
        filters.personFilter,
        filters.companyFilter,
        filters.checkInLocationFilter,
        filters.checkOutLocationFilter,
        filters.sortBy,
        filters.sortDirection,
        filters.activeShifts
      );
      setShifts(response.data.content);
      setTotalElements(response.data.totalElements);
    } catch (error) {
      console.error('Failed to fetch shifts:', error);
      setError(t('shifts.errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [page, filters, t]);

  useEffect(() => {
    fetchShifts();
    let interval;
    
    if (autoRefresh) {
      interval = setInterval(fetchShifts, REFRESH_INTERVAL);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [fetchShifts, autoRefresh]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <Container maxWidth="xxl" sx={{ mt: 4, mb: 4, flex: 1 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={9}>
            <Paper sx={{ 
              p: 2,
              minHeight: {
                // Filter controls (70px) + Table header (53px) + 10 rows (53px * 10) + Pagination (52px) + Padding buffer (40px)
                xs: '695px'
              },
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              <ShiftTable
                shifts={shifts}
                loading={loading}
                page={page}
                totalElements={totalElements}
                onPageChange={setPage}
                filters={filters}
                onFilterChange={handleFilterChange}
                autoRefresh={autoRefresh}
                onAutoRefreshChange={setAutoRefresh}
                onRefresh={fetchShifts}
                showTruckEnter={showTruckEnter}
                showVanEnter={showVanEnter}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} lg={3}>
            <Paper sx={{ 
              p: 2, 
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden' 
            }}>
              <ReportPanel />
            </Paper>
          </Grid>
        </Grid>
      </Container>
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;